import type {
  ForwardRefExoticComponent,
  ForwardRefRenderFunction,
  MutableRefObject,
  PropsWithoutRef,
  RefAttributes,
} from "react";
import { forwardRef, useEffect, useRef } from "react";

export default function useEnsuredForwardedRef<T>(
  forwardedRef: MutableRefObject<T>,
): MutableRefObject<T> {
  const ensuredRef = useRef(forwardedRef && forwardedRef.current);

  useEffect(() => {
    if (!forwardedRef) {
      return;
    }
    forwardedRef.current = ensuredRef.current;
  }, [forwardedRef]);

  return ensuredRef;
}

export function ensuredForwardRef<T, P = {}>(
  Component: ForwardRefRenderFunction<T, P>,
): ForwardRefExoticComponent<PropsWithoutRef<P> & RefAttributes<T>> {
  return forwardRef<T, P>((props, ref) => {
    const ensuredRef = useEnsuredForwardedRef(ref as MutableRefObject<T>);
    return Component(props as P, ensuredRef);
  }) as ForwardRefExoticComponent<PropsWithoutRef<P> & RefAttributes<T>>;
}
